// MixedFamilyRationalToSinglePowerTask.js
// Mieszane czynniki (wymierne wykładniki) → jedna potęga w rodzinie {2,3,5}.
// - wykładniki zawsze skracane (gcd)
// - minus przed ułamkiem (a^{-p/q}, nie a^{-(p/q)} w latexie)
// - Krok 1: ":" -> "·" i 1/(...) -> (...)^{-...}
// - checkAnswer porównuje wykładniki jako liczby wymierne
// - brak generowania i wyświetlania potęgi pierwszej (^1)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// ---------- utils ----------
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

const gcd = (a, b) => {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
};
const simplifyFraction = (num, den) => {
  const s = num < 0 ? -1 : 1;
  const n = Math.abs(num);
  const g = gcd(n, den);
  return { num: s * (n / g), den: den / g };
};
const addR = (aNum, aDen, bNum, bDen) => {
  const num = aNum * bDen + bNum * aDen;
  const den = aDen * bDen;
  return simplifyFraction(num, den);
};
const mulIntR = (k, nNum, nDen) => simplifyFraction(k * nNum, nDen);

// ładny latex dla wykładnika (minus przed ułamkiem)
const expLatex = (num, den = 1) => {
  const { num: nn, den: dd } = simplifyFraction(num, den);
  const sign = nn < 0 ? "-" : "";
  const a = Math.abs(nn);
  if (a % dd === 0) return `${sign}${a / dd}`;
  return `${sign}\\tfrac{${a}}{${dd}}`;
};

// zamiana np. "3" / "-197/15" na {num,den}
const parseRationalString = (s) => {
  const m1 = String(s).match(/^(-?\d+)$/);
  if (m1) return { num: parseInt(m1[1], 10), den: 1 };
  const m2 = String(s).match(/^(-?)(\d+)\/(\d+)$/);
  if (m2) {
    const sign = m2[1] === "-" ? -1 : 1;
    return simplifyFraction(sign * parseInt(m2[2], 10), parseInt(m2[3], 10));
  }
  return null;
};

// ---------- baza/rodziny ----------
const EQUIV = {
  4:   { base: 2, power: 2 },
  8:   { base: 2, power: 3 },
  9:   { base: 3, power: 2 },
  27:  { base: 3, power: 3 },
  25:  { base: 5, power: 2 },
  125: { base: 5, power: 3 },
};
function normalizeToFamily(base, expNum, expDen) {
  if (base === 2 || base === 3 || base === 5) return { base, num: expNum, den: expDen };
  const eq = EQUIV[base];
  if (!eq) return null;
  // (a^r)^(p/q) => base^(r*p/q)
  const { num, den } = simplifyFraction(expNum * eq.power, expDen);
  return { base: eq.base, num, den };
}

// ---------- parsowanie wpisów użytkownika ----------
function parseUser(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, "");

  // 1) 1/(a^(p/q))
  let m = s.match(/^1\/\((\d+)\^\((\-?\d+)\/(\d+)\)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = -parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    const { num: n, den: d } = simplifyFraction(num, den);
    return { base, num: n, den: d };
  }

  // 2) 1/a^(p/q)
  m = s.match(/^1\/(\d+)\^\((\-?\d+)\/(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = -parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    const { num: n, den: d } = simplifyFraction(num, den);
    return { base, num: n, den: d };
  }

  // 3) a^(p/q)
  m = s.match(/^(\d+)\^\((\-?\d+)\/(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    const { num: n, den: d } = simplifyFraction(num, den);
    return { base, num: n, den: d };
  }

  // 4) a^p/q  (interpretuje jako a^(p/q); z minusem przez "^-")
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = s.includes("^-") ? -parseInt(m[2], 10) : parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    const { num: n, den: d } = simplifyFraction(num, den);
    return { base, num: n, den: d };
  }

  // 5) 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const k = parseInt(m[2], 10);
    return { base, num: -k, den: 1 };
  }

  // 6) a^-k  /  a^k
  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const k = parseInt(m[2], 10);
    const isNeg = s.includes("^-");
    return { base, num: isNeg ? -k : k, den: 1 };
  }

  return null;
}

// ładny podgląd wpisu użytkownika
function prettyUserLatex(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/\s/g, "");

  // 1/(a^(p/q))
  let m = s.match(/^1\/\((\d+)\^\((\-?\d+)\/(\d+)\)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{\\left(${expLatex(parseInt(m[2],10), parseInt(m[3],10))}\\right)}}`;

  // 1/a^(p/q)
  m = s.match(/^1\/(\d+)\^\((\-?\d+)\/(\d+)\)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${expLatex(parseInt(m[2],10), parseInt(m[3],10))}}`;

  // a^(p/q)
  m = s.match(/^(\d+)\^\((\-?\d+)\/(\d+)\)$/);
  if (m) return `${m[1]}^{${expLatex(parseInt(m[2],10), parseInt(m[3],10))}}`;

  // a^p/q
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const num = s.includes("^-") ? -parseInt(m[2], 10) : parseInt(m[2], 10);
    return `${m[1]}^{${expLatex(num, parseInt(m[3], 10))}}`;
  }

  // 1/a^k
  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  // a^-k / a^k
  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const isNeg = s.includes("^-");
    return `${m[1]}^{${isNeg ? "-" : ""}${m[2]}}`;
  }

  return s;
}

// ---------- generator ----------
export function generateTask() {
  const familyBase = pick([2, 3, 5]);
  const fam = familyBase === 2
    ? { a: 2, a2: 4, a3: 8, r2: 2, r3: 3 }
    : familyBase === 3
    ? { a: 3, a2: 9, a3: 27, r2: 2, r3: 3 }
    : { a: 5, a2: 25, a3: 125, r2: 2, r3: 3 };

  const candidates = [
    { num: fam.a,  powMul: 1 },
    { num: fam.a2, powMul: fam.r2 },
    { num: fam.a3, powMul: fam.r3 },
  ];

  function makeTerm({ preferFraction = false } = {}) {
    const baseObj = pick(candidates);

    // losujemy wymierny wykładnik p/q i skracamy
    let p = pick(range(1, 9));
    let q = pick([2, 3, 4, 5, 6]);
    ({ num: p, den: q } = simplifyFraction(p, q));

    const sign = Math.random() < 0.35 ? -1 : 1;

    // 🚫 zakaz potęgi pierwszej w treści: jeśli miałoby wyjść +1, losuj p jeszcze raz
    if (sign === 1 && p === q) {
      const pool = range(1, 9).map(x => simplifyFraction(x, q))
        .filter(fr => fr.num !== fr.den); // odrzuć 1
      const picked = pick(pool);
      p = picked.num; q = picked.den;
    }
    const pSigned = sign * p;

    const type = preferFraction ? "frac" : pick(["normal", "divide", "frac"]);

    let latex, contrib, meta;
    if (type === "normal") {
      // a^{p/q} — bez ^1 w zapisie
      const e = expLatex(pSigned, q);
      latex = (e === "1") ? `${baseObj.num}` : `${baseObj.num}^{${e}}`;
      contrib = { num: baseObj.powMul * pSigned, den: q }; // (a^r)^(p/q) => base^(r*p/q)
      meta = {
        original: latex,
        toBase: `(${familyBase}^{${baseObj.powMul}})^{${expLatex(pSigned, q)}}`,
        toBaseNeg: `(${familyBase}^{${baseObj.powMul}})^{${expLatex(pSigned, q)}}`, // normal bez zmiany znaku
        simplified: `${familyBase}^{${expLatex(baseObj.powMul * pSigned, q)}}`,
      };
    } else if (type === "divide") {
      // dzielnik — pokazujemy dodatni wykładnik, minus dopiero w Kroku 1
      const pPos = Math.abs(p); // dodatni
      const ePos = expLatex(pPos, q);
      latex = (ePos === "1") ? `${baseObj.num}` : `${baseObj.num}^{${ePos}}`;
      const neg = simplifyFraction(-(baseObj.powMul * pPos), q);
      contrib = { num: neg.num, den: neg.den };
      meta = {
        original: latex,
        toBase: `(${familyBase}^{${baseObj.powMul}})^{${expLatex(pPos, q)}}`,
        toBaseNeg: `(${familyBase}^{${baseObj.powMul}})^{${expLatex(-pPos, q)}}`, // po Kroku 1 minus
        simplified: `${familyBase}^{${expLatex(neg.num, neg.den)}}`,
        asDivision: true,
      };
    } else {
      // 1 / a^{p/q} – pokazujemy dodatni p/q, minus dopiero w Kroku 1
      const pPos = Math.abs(p);
      const ePos = expLatex(pPos, q);
      latex = `\\dfrac{1}{${ (ePos==="1") ? `${baseObj.num}` : `${baseObj.num}^{${ePos}}` }}`;
      const neg = simplifyFraction(-(baseObj.powMul * pPos), q);
      contrib = { num: neg.num, den: neg.den };
      meta = {
        original: latex,
        toBase: `\\dfrac{1}{(${familyBase}^{${baseObj.powMul}})^{${expLatex(pPos, q)}}}`,
        toBaseNeg: `(${familyBase}^{${baseObj.powMul}})^{${expLatex(-pPos, q)}}`,
        simplified: `${familyBase}^{${expLatex(neg.num, neg.den)}}`,
        isFraction: true,
      };
    }

    // przyda się "po Kroku 1" w wyjaśnieniach
    const originalAfterStep1 = toNegExponentLatex(latex, type);
    return { latex, contrib, meta, type, originalAfterStep1 };
  }

  // --- SEQ: dokładnie 3 czynniki, pierwszy zawsze ułamek ---
  function buildSEQ() {
    const terms = [];
    for (let i = 0; i < 3; i++) terms.push(makeTerm({ preferFraction: i === 0 }));

    const ops = [];
    for (let i = 0; i < 2; i++) ops.push(terms[i + 1].type === "divide" ? ":" : "·");

    let K = { num: 0, den: 1 };
    terms.forEach((t) => { K = addR(K.num, K.den, t.contrib.num, t.contrib.den); });

    let leftLatex = "";
    for (let i = 0; i < terms.length; i++) {
      leftLatex += terms[i].latex;
      if (i < ops.length) leftLatex += (ops[i] === ":" ? "\\,:\\," : "\\,\\cdot\\,");
    }

    return { pattern: "SEQ", base: fam.a, leftLatex, K, seq: { terms, ops } };
  }

  // --- PAREN: część w nawiasie ^t + czynniki na zewnątrz = 3 razem ---
  function buildPAREN() {
    const t = pick([2, 3, 4]);
    const innerCount = pick([1, 2]);
    const outerCount = 3 - innerCount;

    const inner = [];
    const outer = [];
    for (let i = 0; i < innerCount; i++) inner.push(makeTerm({ preferFraction: i === 0 }));
    for (let i = 0; i < outerCount; i++) outer.push(makeTerm());

    const innerOps = [];
    for (let i = 0; i < inner.length - 1; i++) innerOps.push(inner[i + 1].type === "divide" ? ":" : "·");

    let innerLatex = "";
    for (let i = 0; i < inner.length; i++) {
      innerLatex += inner[i].latex;
      if (i < innerOps.length) innerLatex += innerOps[i] === ":" ? "\\,:\\," : "\\,\\cdot\\,";
    }

    let leftLatex = `\\big(${innerLatex}\\big)^{${t}}`;

    const outerOps = [];
    for (let i = 0; i < outer.length; i++) {
      const op = outer[i].type === "divide" ? ":" : "·";
      outerOps.push(op);
      leftLatex += op === ":" ? "\\,:\\," : "\\,\\cdot\\,";
      leftLatex += outer[i].latex;
    }

    // sumy wkładów
    let innerSum = { num: 0, den: 1 };
    inner.forEach((x) => { innerSum = addR(innerSum.num, innerSum.den, x.contrib.num, x.contrib.den); });
    const innerMul = mulIntR(t, innerSum.num, innerSum.den);

    let outerSum = { num: 0, den: 1 };
    outer.forEach((x) => { outerSum = addR(outerSum.num, outerSum.den, x.contrib.num, x.contrib.den); });

    const K = addR(innerMul.num, innerMul.den, outerSum.num, outerSum.den);

    return {
      pattern: "PAREN",
      base: fam.a,
      leftLatex,
      K,
      paren: { t, inner, innerOps, innerSum, innerMul, outer, outerOps, outerSum },
    };
  }

  const built = (pick(["SEQ", "PAREN"]) === "SEQ") ? buildSEQ() : buildPAREN();
  return {
    type: "MixedFamilyRationalToSinglePowerTask",
    data: { base: fam.a, familyBase, leftLatex: built.leftLatex, K: built.K, __meta: built }
  };
}

// ---------- rendery ----------
export function renderTask({ data }) {
  const { base, leftLatex } = data;
  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={`${base}`} />:
      </div>
      <BlockMath math={leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const { base, K } = task.data;
  const ph = `${base}^{${expLatex(K.num, K.den)}}`;
  const phAlt = `1/${base}^{${expLatex(Math.abs(K.num), K.den)}}`;
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={(ans) => onChange?.(ans)}
        disabled={disabled}
        placeholder={"np. 5^2/3 lub 5^-2/3"}
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={prettyUserLatex(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  const { base, K } = data;
  return <InlineMath math={`${base}^{${expLatex(K.num, K.den)}}`} />;
}

// ---------- check ----------
function sameR(aNum, aDen, bNum, bDen) {
  return aNum * bDen === bNum * aDen;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const parsed = parseUser(userAnswer.raw);
  if (!parsed) return false;

  // normalizacja do rodziny {2,3,5}
  const norm = normalizeToFamily(parsed.base, parsed.num, parsed.den);
  if (!norm) return false;

  const targetBase = data.base;
  const { num: Tn, den: Td } = simplifyFraction(data.K.num, data.K.den);
  const { num: Un, den: Ud } = simplifyFraction(norm.num, norm.den);

  return norm.base === targetBase && sameR(Tn, Td, Un, Ud);
}

// ---------- wyjaśnienia ----------
function toNegExponentLatex(termLatex, termType) {
  // Zamieniamy ":"/ułamek na mnożenie z ujemnym wykładnikiem bez zmiany podstawy.
  if (termType === "divide") {
    // 1) "n" -> "n^{-1}"
    const m0 = termLatex.match(/^(\d+)$/);
    if (m0) return `${m0[1]}^{-1}`;
    // 2) "n^{p/q}" -> "n^{-p/q}"
    const m = termLatex.match(/^(\d+)\^\{(.+)\}$/);
    if (m) {
      const r = parseRationalString(m[2].replace(/\\tfrac\{(\d+)\}\{(\d+)\}/, "$1/$2"));
      if (r) return `${m[1]}^{${expLatex(-r.num, r.den)}}`;
    }
    return termLatex;
  }
  if (termType === "frac") {
    // \dfrac{1}{n^{p/q}} -> n^{-p/q}
    const m = termLatex.match(/^\\dfrac\{1\}\{(\d+)\^\{(.+)\}\}$/);
    if (m) {
      const r = parseRationalString(m[2].replace(/\\tfrac\{(\d+)\}\{(\d+)\}/, "$1/$2"));
      if (r) return `${m[1]}^{${expLatex(-r.num, r.den)}}`;
    }
    // oraz \dfrac{1}{n} -> n^{-1}
    const m0 = termLatex.match(/^\\dfrac\{1\}\{(\d+)\}$/);
    if (m0) return `${m0[1]}^{-1}`;
    return termLatex;
  }
  return termLatex;
}

export function renderExplanation({ data }) {
  const { base, leftLatex, K, __meta } = data;

  const hint =
    base === 2 ? '4=2^{2},\\;8=2^{3}'
    : base === 3 ? '9=3^{2},\\;27=3^{3}'
    : '25=5^{2},\\;125=5^{3}';

  const ruleLine =
    'a^{m}\\cdot a^{n}=a^{m+n}\\quad a^{m}:a^{n}=a^{m-n}\\quad (a^{r})^{t}=a^{r\\cdot t}\\quad \\dfrac{1}{a^{n}}=a^{-n}';

  // KROK 1: ":" -> "·" i 1/(...) -> (...)^{-...}
  let step1Needed = false;
  let step1Latex = "";

  if (__meta.pattern === "SEQ") {
    const pieces = __meta.seq.terms.map((t) => {
      if (t.type === "divide" || t.type === "frac") {
        step1Needed = true;
        return toNegExponentLatex(t.latex, t.type);
      }
      return t.latex;
    });
    step1Latex = pieces.join('\\,\\cdot\\,');
  } else {
    const innerPieces = __meta.paren.inner.map((t) => {
      if (t.type === "divide" || t.type === "frac") { step1Needed = true; return toNegExponentLatex(t.latex, t.type); }
      return t.latex;
    });
    const innerJoined = innerPieces.join('\\,\\cdot\\,');
    const outerPieces = __meta.paren.outer.map((t) => {
      if (t.type === "divide" || t.type === "frac") { step1Needed = true; return toNegExponentLatex(t.latex, t.type); }
      return t.latex;
    });
    step1Latex = `\\big(${innerJoined}\\big)^{${__meta.paren.t}}` + (outerPieces.length ? `\\,\\cdot\\,${outerPieces.join('\\,\\cdot\\,')}` : "");
  }

  // Krok szczegółowy per-składnik (po sprowadzeniu do rodziny)
  const lines = [];

  const pushTermLines = (t) => {
    // Po Kroku 1 dzielniki/łamki mają już ujemny wykładnik:
    const lhs = (t.type === "divide" || t.type === "frac") ? (t.originalAfterStep1) : t.meta.original;
    const toBaseLine = (t.type === "divide" || t.type === "frac") ? (t.meta.toBaseNeg ?? t.meta.toBase) : t.meta.toBase;
    lines.push(`${lhs}\\;=\\;${toBaseLine}\\;=\\;${t.meta.simplified}`);

    // jeśli w simplified pojawia się nieskrócony ułamek, dołóż krok skracania
    const mm = t.meta.simplified.match(/^\s*\d+\^\{(-?)(\d+)\/(\d+)\}\s*$/);
    if (mm) {
      const sign = mm[1] === "-" ? -1 : 1;
      const { num, den } = simplifyFraction(sign * parseInt(mm[2], 10), parseInt(mm[3], 10));
      const red = `${base}^{${expLatex(num, den)}}`;
      if (t.meta.simplified !== red) lines.push(`=\\;${red}`);
      if (Math.abs(num) === den) { // = ±1
        const k = num / den;
        lines.push(`=\\;${base}^{${k}}`);
        if (k === 1) lines.push(`=\\;${base}`);
      }
    }
  };

  if (__meta.pattern === "SEQ") {
    __meta.seq.terms.forEach(pushTermLines);

    const contribs = __meta.seq.terms.map(t => t.contrib);
    const factorLatex = contribs
      .map(c => `${base}^{${expLatex(c.num, c.den)}}`)
      .join('\\,\\cdot\\,');
    lines.push(factorLatex);

    const sumStr = contribs.reduce((acc, c, i) => {
      const e = expLatex(c.num, c.den);
      return acc + (i ? (e.startsWith("-") ? ` ${e}` : ` +${e}`) : e);
    }, "");
    lines.push(`=\\;${base}^{${sumStr}}`);

    const { num: kn, den: kd } = simplifyFraction(K.num, K.den);
    lines.push(`=\\;${base}^{${expLatex(kn, kd)}}`);

  } else {
    const inner = __meta.paren.inner;
    const outer = __meta.paren.outer;

    inner.forEach(pushTermLines);

    const innerContribs = inner.map(t => t.contrib);
    const innerFactors = innerContribs.map(c => `${base}^{${expLatex(c.num, c.den)}}`).join('\\,\\cdot\\,');
    lines.push(`\\big(${innerFactors}\\big)^{${__meta.paren.t}}`);

    const innerSum = innerContribs.reduce((S, c) => addR(S.num, S.den, c.num, c.den), { num: 0, den: 1 });
    const innerMul = mulIntR(__meta.paren.t, innerSum.num, innerSum.den);
    lines.push(`=\\;${base}^{(${expLatex(innerSum.num, innerSum.den)})\\cdot ${__meta.paren.t}}`);
    lines.push(`=\\;${base}^{${expLatex(innerMul.num, innerMul.den)}}`);

    outer.forEach(pushTermLines);

    const outerContribs = outer.map(t => t.contrib);
    const factors = [`${base}^{${expLatex(innerMul.num, innerMul.den)}}`, ...outerContribs.map(c => `${base}^{${expLatex(c.num, c.den)}}`)];
    lines.push(factors.join('\\,\\cdot\\,'));

    const Kexp = expLatex(K.num, K.den);
    lines.push(`=\\;${base}^{${Kexp}}`);
  }

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      {step1Needed && (
        <>
          <p><strong>Krok 1:</strong> Zamień dzielenie na mnożenie przez odwrotność (dopisz minus do wykładnika).</p>
          <BlockMath math={`${leftLatex}\\;=\\;${step1Latex}`} />
        </>
      )}

      <p><strong>Krok {step1Needed ? 2 : 1}:</strong> Zamień wszystko na wspólną podstawę.</p>
      <BlockMath math={hint} />

      <p><strong>Krok {step1Needed ? 3 : 2}:</strong> Zasady łączenia:</p>
      <BlockMath math={ruleLine} />

      <p><strong>Krok {step1Needed ? 4 : 3}:</strong> Szczegółowy rachunek krok po kroku:</p>
      <BlockMath math={leftLatex} />
      {lines.map((ln, idx) => <BlockMath key={idx} math={ln} />)}
    </div>
  );
}
